// The editor's write sequence: entity write -> reload + snapshot refresh -> success / onChanged,
// run as one operation that a second click cannot start again. Pure (no React / app runtime) so it
// is node-tested.

export type EntityWriteOutcome = "done" | "refreshFailed" | "writeFailed";

/**
 * Runs the entity `write`, then `refresh` (resolves false when it failed and shows its own error).
 * - write failed: nothing else runs (the write's own error is shown by its caller);
 * - refresh failed: `onChanged` only (the entity did change), no success message;
 * - both done: `onSucceeded`, then `onChanged`.
 */
export async function runEntityWrite(params: {
  refresh: () => Promise<boolean>;
  write: () => Promise<unknown>;
  onChanged: () => void;
  onSucceeded: () => void;
}): Promise<EntityWriteOutcome> {
  try {
    await params.write();
  } catch {
    return "writeFailed";
  }
  const refreshed = await params.refresh();
  if (refreshed) {
    params.onSucceeded();
  }
  params.onChanged();
  return refreshed ? "done" : "refreshFailed";
}

export interface SingleFlight {
  readonly isBusy: boolean;
  /** Runs `fn` unless a previous run is still in flight; then resolves `undefined` at once. */
  run<T>(fn: () => Promise<T>): Promise<T | undefined>;
}

/** Synchronous busy flag: guards against a second click landing before React re-renders. */
export function createSingleFlight(): SingleFlight {
  let busy = false;
  return {
    get isBusy() {
      return busy;
    },
    async run<T>(fn: () => Promise<T>): Promise<T | undefined> {
      if (busy) {
        return undefined;
      }
      busy = true;
      try {
        return await fn();
      } finally {
        busy = false;
      }
    },
  };
}
